import React, { useEffect, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import { enqueueSnackbar } from "notistack";
import UploadIcon from '@mui/icons-material/Upload';
import LoopIcon from '@mui/icons-material/Loop';

import CustomAutoComplete from "../CommonComponents/CustomAutoComplete";
import DividerLabel from "../CommonComponents/DividerLabel";

import { TargetStorage } from "../../../types/targetStorageTypes";
import { CollectionClip } from "../../../types/collectionTypes";
import { AGGREGATION_USAGE_TYPE } from "./SocialPublish";

import { getStorages, publishToTargetStorage } from "../../../queries/targetStorageQueries";

interface TargetStoragePublishSectionProps {
    logicClip: CollectionClip | null;
    usageType: AGGREGATION_USAGE_TYPE;
    onPublicationAdd: (newPost: any) => void;
}

const STORAGE_LABELS: Record<string, string> = {
    s3: "Amazon S3",
    azure: "Azure Blob",
    sftp: "SFTP Server"
};

export const TargetStoragePublishSection: React.FC<TargetStoragePublishSectionProps> = ({
                                                                                            logicClip,
                                                                                            usageType,
                                                                                            onPublicationAdd
                                                                                        }) => {
    const isPreset = usageType === AGGREGATION_USAGE_TYPE.PRESET;

    const [storages, setStorages] = useState<TargetStorage[]>([]);
    const [selectedType, setSelectedType] = useState<{ id: string, value: string } | null>(null);
    const [selectedAccount, setSelectedAccount] = useState<{ id: string, value: string } | null>(null);

    const [loadingStorages, setLoadingStorages] = useState(false);
    const [isPublishing, setIsPublishing] = useState(false);

    // 1. Caricamento degli storage disponibili tramite la GET
    useEffect(() => {
        const fetchStorages = async () => {
            setLoadingStorages(true);
            try {
                const data = await getStorages();
                setStorages(data);
            } catch (error) {
                enqueueSnackbar("Errore durante il caricamento degli storage");
            } finally {
                setLoadingStorages(false);
            }
        };
        fetchStorages();
    }, []);

    // 2. Calcolo dei tipi di storage disponibili in base ai risultati
    const availableTypes = Array.from(new Set(storages.map(s => s.type))).map(type => ({
        id: type,
        value: STORAGE_LABELS[type] || type
    }));

    // 3. Calcolo degli account disponibili per il tipo selezionato
    const availableAccounts = storages
        .filter(s => selectedType && s.type === selectedType.id)
        .map(s => ({
            id: s.id!,
            value: s.name
        }));

    // Reset dell'account se cambi il tipo
    useEffect(() => {
        setSelectedAccount(null);
    }, [selectedType]);

    const handlePublish = async () => {
        if (!selectedAccount || !logicClip?.materializations?.[0]?.id) return;

        setIsPublishing(true);
        try {
            const materializationId = logicClip.materializations[0].id;

            // Chiamata all'endpoint POST /publish/:materialization_id scritto precedentemente
            const response = await publishToTargetStorage(materializationId, selectedAccount.id);

            // Passiamo la risposta a onPublicationAdd per far scattare il polling nella UI principale
            onPublicationAdd(response);
            enqueueSnackbar('Pubblicazione sullo storage avviata con successo!');

        } catch (error) {
            enqueueSnackbar('Impossibile avviare la pubblicazione');
        } finally {
            setIsPublishing(false);
        }
    };

    const spinAnimation = { animation: "spin 2s linear infinite", "@keyframes spin": { "100%": { transform: "rotate(360deg)" } } };

    return (
        <Box sx={{ p: 2, borderColor: 'divider', borderRadius: 1, mt: 2, mb: 2 }}>
            <DividerLabel label={
                               <Box display="flex" alignItems="center" gap={1}>
                                  <Typography variant="h5" fontWeight="bold">
                                      Target Storage Publication
                                  </Typography>
                              </Box>
                          } />

            <Grid container spacing={4} sx={{ mt: 3, mb: 2 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                    <CustomAutoComplete
                        label="Storage Type"
                        options={availableTypes}
                        value={selectedType}
                        onChange={(_, v) => setSelectedType(v as { id: string, value: string } | null)}
                        labelField="value"
                        valueField="id"
                        multiple={false}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <CustomAutoComplete
                        label="Storage Account"
                        options={availableAccounts}
                        value={selectedAccount}
                        onChange={(_, v) => setSelectedAccount(v as { id: string, value: string } | null)}
                        labelField="value"
                        valueField="id"
                        multiple={false}
                        disabled={!selectedType || availableAccounts.length === 0}
                    />
                </Grid>
            </Grid>

            <Box display="flex" justifyContent="flex-end" mt={2}>
                <Button
                    variant="contained"
                    size="small"
                    onClick={handlePublish}
                    disabled={isPublishing || !selectedAccount || isPreset || loadingStorages}
                    startIcon={isPublishing ? <LoopIcon sx={spinAnimation} /> : <UploadIcon />}
                >
                    {isPreset ? 'Save Preset' : 'Publish to Storage'}
                </Button>
            </Box>
        </Box>
    );
};